import {
  dailyChallengeForToday,
  dailyStreakFromProgress,
  streakMilestone,
  type DailyChallenge,
  type DailyStreak,
} from '../game/dailyChallenge';
import { problemTypeLabel, problemTypeRule, worldById } from '../models/world';
import { type ProgressMap } from '../services/progress';
import { escapeHtml } from './puzzleScreen';

const DAY_MS = 24 * 60 * 60 * 1000;

const MILESTONE_LABEL = {
  none: '',
  bronze: 'Bronze runner',
  silver: 'Silver runner',
  gold: 'Gold runner',
} as const;

const dateFormat = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

const shortDateFormat = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});

export function formatDailyDate(challenge: DailyChallenge): string {
  return dateFormat.format(challenge.date);
}

export function formatDailyDateShort(challenge: DailyChallenge): string {
  return shortDateFormat.format(challenge.date);
}

export function timeUntilNextDispatch(now: Date = new Date()): string {
  const remaining = DAY_MS - (now.getTime() % DAY_MS);
  const hours = Math.floor(remaining / 3_600_000);
  const minutes = Math.floor((remaining % 3_600_000) / 60_000);
  return hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;
}

export function streakMarkup(streak: DailyStreak): string {
  const milestone = MILESTONE_LABEL[streakMilestone(streak)];
  const days = (n: number): string => `${n} ${n === 1 ? 'day' : 'days'}`;
  return `
    <dl class="streak">
      <div><dt>Current streak</dt><dd>${days(streak.currentStreak)}</dd></div>
      <div><dt>Longest</dt><dd>${days(streak.longestStreak)}</dd></div>
      <div><dt>Dispatches run</dt><dd>${streak.completedDates.size}</dd></div>
    </dl>
    ${milestone ? `<p class="streak__rank">${milestone}</p>` : ''}
  `;
}

export interface DailyBriefingView {
  readonly progress: ProgressMap;
  readonly online: boolean;
}

export function dailyBriefingMarkup(view: DailyBriefingView): string {
  const challenge = dailyChallengeForToday();
  const world = worldById(challenge.worldIndex);
  const today = view.progress[challenge.levelId];
  const streak = dailyStreakFromProgress(view.progress);
  const done = today !== undefined && today.stars > 0;

  const status = done
    ? `
      <p class="brief__status">
        Delivered today: <b>${'★'.repeat(today.stars)}${'☆'.repeat(3 - today.stars)}</b>
        · best ${escapeHtml(String(today.bestScore))}
      </p>
      <p class="brief__next">Next dispatch in ${timeUntilNextDispatch()}.</p>
    `
    : `<p class="brief__next">Open until midnight UTC, in ${timeUntilNextDispatch()}.</p>`;

  return `
    <div class="screen screen--brief">
      <header class="sub-header">
        <button class="btn btn--ghost btn--icon" data-action="back" aria-label="Back to the map">←</button>
        <div>
          <h1>Daily Dispatch</h1>
          <p>${escapeHtml(formatDailyDate(challenge))}</p>
        </div>
      </header>
      <article class="note note--order brief__order">
        <span class="note__staple" aria-hidden="true"></span>
        <p class="note__kicker">Today's front · ${escapeHtml(world?.name ?? 'Gearfall')}</p>
        <h2 class="note__title">${escapeHtml(world ? problemTypeLabel(world.problemType) : 'Unknown')}</h2>
        <p class="note__body">
          One generated run, the same for every runner in Gearfall. Difficulty ${challenge.difficulty} of 10.
        </p>
        ${world ? `<p class="note__rule">${escapeHtml(problemTypeRule(world.problemType))}</p>` : ''}
      </article>
      <section class="brief__streak" aria-label="Streak">
        ${streakMarkup(streak)}
        ${status}
        ${view.online ? '' : '<p class="hero__offline">Offline — today\'s run is saved on this device only.</p>'}
      </section>
      <div class="brief__actions">
        <button class="btn ${done ? 'btn--ghost' : 'btn--primary'}" data-action="play">
          ${done ? 'Run it again' : 'Take the dispatch'}
        </button>
      </div>
    </div>
  `;
}
